"use client"

import type React from "react"
import { useNavigate } from 'react-router-dom'
import {
    AppBar,
    Toolbar,
    IconButton,
    InputBase,
    Box,
    useMediaQuery,
    useTheme,
    Tooltip,
    InputAdornment,
    alpha,
    styled,
    Container
} from "@mui/material"
import {
    IconSearch,
    IconBrandFacebook,
    IconBrandInstagram,
    IconBrandTiktok,
    IconBrandWhatsapp,
    IconX,
} from "@tabler/icons-react"
import { useEffect, useState } from "react"

interface HeaderProps {
    logoUrl: string
    onSearch: (term: string) => void
    searchTerm: string
}

const Search = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
    position: "relative",
    borderRadius: "30px",
    backgroundColor: alpha(theme.palette.common.white, 0.9),
    "&:hover": {
        backgroundColor: alpha(theme.palette.common.white, 1),
    },
    marginRight: isMobile ? 0 : theme.spacing(2),
    marginLeft: 0,
    width: "100%",
    [theme.breakpoints.up("sm")]: {
        marginLeft: theme.spacing(3),
        width: "auto",
    },
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e0e0e0",
    display: "flex",
    alignItems: "center",
}))

const SearchIconWrapper = styled("div")(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
}))

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: theme.palette.text.primary,
    flex: 1,
    "& .MuiInputBase-input": {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: theme.spacing(2),
        transition: theme.transitions.create("width"),
        width: "100%",
        [theme.breakpoints.up("md")]: {
            width: "20ch",
            "&:focus": {
                width: "30ch",
            },
        },
    },
}))

const Header: React.FC<HeaderProps> = ({
    logoUrl,
    onSearch,
    searchTerm,
}) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("md"))
    const navigate = useNavigate()
    const [isToolsPage, setIsToolsPage] = useState(false)

    useEffect(() => {
        const path = window.location.pathname
        setIsToolsPage(path.startsWith('/tools'))
    }, [])

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        onSearch(value)
    }

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                backgroundColor: "#d7ddd1",
                color: "#0d6336",
                zIndex: 1200,
                top: 0, // Ensure it sticks to the top
            }}
            id="header"
        >
            <Container
                maxWidth="lg"
                sx={{
                    px: { xs: 1, sm: 2 },
                }}
            >
                <Toolbar
                    sx={{
                        justifyContent: "space-between",
                        padding: { xs: "6px 0", md: "10px 0" },
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "stretch" : "center",
                        gap: isMobile ? 1 : 0,
                        minHeight: isMobile ? "auto" : "64px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: isMobile ? "space-between" : "flex-start",
                            width: isMobile ? "100%" : "auto",
                        }}
                    >
                        <Box
                            sx={{
                                width: isMobile ? "50px" : "70px",
                                height: isMobile ? "50px" : "70px",
                                borderRadius: "50%",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                            }}
                            onClick={() => navigate('/')}
                            onDoubleClick={() => navigate('/tools')}
                        >
                            <img
                                src={logoUrl || "/placeholder.svg"}
                                alt="E-Kochalita Logo"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        </Box>

                        {isMobile && (
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Tooltip title="Facebook">
                                    <IconButton
                                        href="https://www.facebook.com/ekochalita"
                                        target="_blank"
                                        aria-label="Facebook"
                                        size="small"
                                        sx={{ color: "#3b5998" }}
                                    >
                                        <IconBrandFacebook size={20} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Instagram">
                                    <IconButton
                                        href="https://www.instagram.com/ekochalita"
                                        target="_blank"
                                        aria-label="Instagram"
                                        size="small"
                                        sx={{ color: "#e1306c", padding: "4px" }}
                                    >
                                        <IconBrandInstagram size={20} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="TikTok">
                                    <IconButton
                                        href="https://www.tiktok.com/@ekochalita"
                                        target="_blank"
                                        aria-label="TikTok"
                                        size="small"
                                        sx={{ color: "#000000", padding: "4px" }}
                                    >
                                        <IconBrandTiktok size={20} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="WhatsApp">
                                    <IconButton
                                        href="https://wa.me/59174318810"
                                        target="_blank"
                                        aria-label="WhatsApp"
                                        size="small"
                                        sx={{ color: "#25D366", padding: "4px" }}
                                    >
                                        <IconBrandWhatsapp size={20} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flex: 1,
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 2,
                            mb: isToolsPage ? 0 : 1,
                        }}
                    >
                        {!isToolsPage && (
                            <Search isMobile={isMobile}>
                                <SearchIconWrapper>
                                    <IconSearch />
                                </SearchIconWrapper>
                                <StyledInputBase
                                    placeholder="Buscar cuadritos..."
                                    inputProps={{ "aria-label": "buscar" }}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    endAdornment={
                                        searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onSearch("")}
                                                    sx={{
                                                        color: (theme) =>
                                                            theme.palette.grey[500],
                                                    }}
                                                >
                                                    <IconX size={18} />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                />
                            </Search>
                        )}
                        {!isMobile && (
                            <Box sx={{ display: "flex", gap: 1, mr: 1 }}>
                                <Tooltip title="Facebook">
                                    <IconButton
                                        href="https://www.facebook.com/ekochalita"
                                        target="_blank"
                                        aria-label="Facebook"
                                        size="small"
                                        sx={{ color: "#3b5998" }}
                                    >
                                        <IconBrandFacebook size={22} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Instagram">
                                    <IconButton
                                        href="https://www.instagram.com/ekochalita"
                                        target="_blank"
                                        aria-label="Instagram"
                                        size="small"
                                        sx={{ color: "#e1306c" }}
                                    >
                                        <IconBrandInstagram size={22} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="TikTok">
                                    <IconButton
                                        href="https://www.tiktok.com/@ekochalita"
                                        target="_blank"
                                        aria-label="TikTok"
                                        size="small"
                                        sx={{ color: "#000000" }}
                                    >
                                        <IconBrandTiktok size={22} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="WhatsApp">
                                    <IconButton
                                        href="https://wa.me/59174318810"
                                        target="_blank"
                                        aria-label="WhatsApp"
                                        size="small"
                                        sx={{ color: "#25D366" }}
                                    >
                                        <IconBrandWhatsapp size={22} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    )
}

export default Header

